import {
  ACESFilmicToneMapping,
  CineonToneMapping,
  LinearToneMapping,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  NoToneMapping,
  PCFShadowMap,
  PCFSoftShadowMap,
  ReinhardToneMapping,
  Scene,
  type Light,
  type Material,
  type ToneMapping,
} from "three";
import { DEFAULT_ENVIRONMENT_URL } from "./loadEnvironmentTexture";

type ConfigurableRenderer = {
  toneMapping: ToneMapping;
  toneMappingExposure: number;
  setPixelRatio?: (value: number) => void;
  shadowMap?: {
    enabled: boolean;
    type: number;
  };
};

export type ToneMappingName =
  | "none"
  | "linear"
  | "reinhard"
  | "cineon"
  | "aces";
export type ShadowQuality = "low" | "medium" | "high";
/** SSGI debug/composite output — mirrors three.js webgpu_postprocessing_ssgi. */
export type SsgiOutputMode = "combined" | "direct" | "ao" | "gi" | "normal" | "depth";
export type SsgiSparseEdgeKernel = "cross" | "sobel" | "prewitt" | "scharr";
export type SsgiSparseAdaptive = "off" | "edge" | "refine";
/** "v1" = ilk seyrek GI; "v2" = küçük hedef + kayan nokta + yalnız GI'ya birikim. */
export type SsgiSparseVersion = "v1" | "v2";
export type SsgiSparseOutputMode =
  | "combined"
  | "direct"
  | "gi"
  | "ao"
  | "source"
  | "normal"
  | "depth"
  | "debug";
/**
 * Product settings scope. Each host owns its own defaults + localStorage bag.
 * Hosts call `setProductScope` on connect so changing one default does not
 * rewrite the others.
 */
export type RenderSettingsProductScope =
  | "shared"
  | "roomDesigner"
  | "configurator"
  | "configuratorViewer"
  | "modelViewer"
  | "interiorStudy"
  | "cabin";

export interface RenderSettings {
  toneMapping: ToneMappingName;
  exposure: number;
  environmentIntensity: number;
  /** Equirect env map URL (.png / .hdr / .exr). */
  environmentUrl: string;
  aoMapIntensity: number;
  /**
   * Çizim tamponu = CSS boyut × devicePixelRatio × bu katsayı.
   * 1 = tam cihaz çözünürlüğü.
   */
  pixelRatioScale: number;
  /** Screen-space GI + AO (WebGPU). Mutually exclusive with `gtao`. */
  ssgi: {
    enabled: boolean;
    output: SsgiOutputMode;
    sliceCount: number;
    stepCount: number;
    radius: number;
    thickness: number;
    expFactor: number;
    aoIntensity: number;
    giIntensity: number;
    backfaceLighting: number;
    useLinearThickness: boolean;
    useScreenSpaceSampling: boolean;
    useTemporalFiltering: boolean;
  };
  /**
   * Sparse GI (v1: GI only; v2: GI + AO). Exclusive with classic `ssgi` / GTAO / path.
   */
  ssgiSparse: {
    enabled: boolean;
    output: SsgiSparseOutputMode;
    sliceCount: number;
    stepCount: number;
    radius: number;
    thickness: number;
    expFactor: number;
    giIntensity: number;
    backfaceLighting: number;
    useLinearThickness: boolean;
    useScreenSpaceSampling: boolean;
    useTemporalFiltering: boolean;
    giStride: number;
    giAdaptive: SsgiSparseAdaptive;
    giEdgeThickness: number;
    giEdgeKernel: SsgiSparseEdgeKernel;
    giEdgeThreshold: number;
    giDepthThreshold: number;
    giNormalThreshold: number;
    giMinOkCount: number;
    version: SsgiSparseVersion;
    /** v2: bir pikselde en fazla kaç kare birikir (1 = birikim yok). */
    historyLength: number;
    /** v2: yumuşatma turu (0–3). */
    denoisePasses: number;
    /** v2: komşu noktası tutmayan pikselleri ayrıca tara. */
    edgeRefine: boolean;
    /** v2: AO üssü (0 = AO yok). */
    aoIntensity: number;
  };
  /** WebGPU GTAO (AoTslEffect). Mutually exclusive with `ssgi` / pathTrace. */
  gtao: {
    enabled: boolean;
    intensity: number;
    radius: number;
    samples: number;
    scale: number;
    thickness: number;
    distanceExponent: number;
    distanceFallOff: number;
    resolutionScale: number;
    useTemporalFiltering: boolean;
    /** WebGL accumulating path: one-shot Poisson denoise after convergence. */
    denoise: boolean;
    lumaPhi: number;
    depthPhi: number;
    normalPhi: number;
    /** Poisson denoise kernel radius (not AO radius). */
    pdRadius: number;
  };
  /**
   * Progressive GPU path tracing (three-gpu-pathtracer).
   * Exclusive with SSGI/GTAO/TAA — raster post turns off while enabled.
   */
  pathTrace: {
    enabled: boolean;
    renderScale: number;
    environmentIntensity: number;
    exposure: number;
    bounces: number;
    samplesPerFrame: number;
    filterGlossyFactor: number;
    fadeDuration: number;
    /** Stop after this many accumulated path-trace frames. */
    frameCount: number;
  };
  taa: {
    enabled: boolean;
    sampleLevel: number;
    unbiased: boolean;
  };
  aa: {
    fxaa: boolean;
    msaa: boolean;
    samples: number;
  };
  /** Room point lights (RoomLightingController). */
  lights: {
    enabled: boolean;
  };
  shadows: {
    enabled: boolean;
    quality: ShadowQuality;
  };
}

export const RENDER_SETTINGS_CHANGED_EVENT = "render-settings-changed";

function storageKeyFor(scope: RenderSettingsProductScope): string {
  return `sugar.render-settings.${scope}.v1`;
}

type SettingsOverrides = Partial<{
  [K in keyof RenderSettings]: RenderSettings[K] extends object
    ? Partial<RenderSettings[K]>
    : RenderSettings[K];
}>;

/** `setTransient` ile verilebilen (persist edilmeyen) skaler ayarlar. */
export type TransientSettings = Partial<
  Pick<RenderSettings, "environmentIntensity" | "exposure" | "environmentUrl">
>;

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object") {
    Object.freeze(value);
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child);
    }
  }
  return value;
}

/** Shared starting template. Change a host via its exported defaults, not here. */
function buildBaseRenderSettings(): RenderSettings {
  return {
    toneMapping: "aces",
    exposure: 1,
    environmentIntensity: 1,
    environmentUrl: DEFAULT_ENVIRONMENT_URL,
    aoMapIntensity: 1,
    pixelRatioScale: 0.5,
    ssgi: {
      enabled: false,
      output: "combined",
      sliceCount: 3,
      stepCount: 8,
      radius: 12,
      thickness: 1,
      expFactor: 2,
      aoIntensity: 1,
      giIntensity: 10,
      backfaceLighting: 0,
      useLinearThickness: false,
      useScreenSpaceSampling: true,
      useTemporalFiltering: true,
    },
    ssgiSparse: {
      enabled: false,
      output: "combined",
      sliceCount: 3,
      stepCount: 8,
      radius: 12,
      thickness: 1,
      expFactor: 2,
      giIntensity: 10,
      backfaceLighting: 0,
      useLinearThickness: false,
      useScreenSpaceSampling: true,
      useTemporalFiltering: true,
      giStride: 2,
      giAdaptive: "off",
      giEdgeThickness: 2,
      giEdgeKernel: "sobel",
      giEdgeThreshold: 0.04,
      giDepthThreshold: 0.25,
      giNormalThreshold: 0.8,
      giMinOkCount: 3,
      version: "v1",
      historyLength: 32,
      denoisePasses: 2,
      edgeRefine: true,
      aoIntensity: 1,
    },
    gtao: {
      enabled: false,
      intensity: 1,
      radius: 0.25,
      samples: 32,
      scale: 1,
      thickness: 1,
      distanceExponent: 2.5,
      distanceFallOff: 1,
      resolutionScale: 1,
      useTemporalFiltering: false,
      denoise: false,
      lumaPhi: 10,
      depthPhi: 2,
      normalPhi: 1,
      pdRadius: 2,
    },
    pathTrace: {
      enabled: false,
      renderScale: 0.75,
      environmentIntensity: 1,
      exposure: 1,
      bounces: 4,
      samplesPerFrame: 1,
      filterGlossyFactor: 0.5,
      fadeDuration: 250,
      frameCount: 128,
    },
    taa: {
      enabled: false,
      sampleLevel: 1,
      unbiased: false,
    },
    aa: {
      fxaa: true,
      msaa: false,
      samples: 1,
    },
    lights: {
      enabled: false,
    },
    shadows: {
      enabled: false,
      quality: "medium",
    },
  };
}

function mergeSettings(
  overrides: SettingsOverrides,
  base: Readonly<RenderSettings>,
): RenderSettings {
  const merged = {
    ...base,
    ...overrides,
    ssgi: { ...base.ssgi, ...overrides.ssgi },
    ssgiSparse: {
      ...base.ssgiSparse,
      ...overrides.ssgiSparse,
    },
    gtao: { ...base.gtao, ...overrides.gtao },
    pathTrace: { ...base.pathTrace, ...overrides.pathTrace },
    taa: { ...base.taa, ...overrides.taa },
    aa: { ...base.aa, ...overrides.aa },
    lights: { ...base.lights, ...overrides.lights },
    shadows: { ...base.shadows, ...overrides.shadows },
  };
  const legacyAa = overrides.aa as
    | { enabled?: boolean; fxaa?: boolean }
    | undefined;
  if (legacyAa && legacyAa.fxaa == null && legacyAa.enabled != null) {
    merged.aa.fxaa = Boolean(legacyAa.enabled);
  }
  merged.pixelRatioScale = Math.min(1, Math.max(0.25, merged.pixelRatioScale));
  merged.aa.samples = Math.min(
    8,
    Math.max(1, Math.round(merged.aa.samples || 1)),
  );
  const requestedFrames = merged.pathTrace.frameCount;
  merged.pathTrace.frameCount =
    requestedFrames === 0
      ? 0
      : Math.min(512, Math.max(16, Math.round(requestedFrames || 128)));
  return enforcePipelineExclusivity(merged);
}

/** Per-host defaults. Edit the named export for that host only. */
function createRenderSettings(
  overrides: SettingsOverrides = {},
): RenderSettings {
  return mergeSettings(overrides, buildBaseRenderSettings());
}

export const DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> = deepFreeze(
  createRenderSettings(),
);

export const ROOM_DESIGNER_DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> =
  deepFreeze(createRenderSettings({ pixelRatioScale: 0.75 }));

export const CONFIGURATOR_DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> =
  deepFreeze(
    createRenderSettings({
      pixelRatioScale: 0.75,
      environmentIntensity: 1.2,
      gtao: { enabled: true },
    }),
  );

export const MODEL_VIEWER_DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> =
  deepFreeze(createRenderSettings({ pixelRatioScale: 0.75 }));

/** Daylight interior study. Isolated bag so room-designer settings stay put. */
export const INTERIOR_STUDY_DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> =
  deepFreeze(
    createRenderSettings({
      toneMapping: "aces",
      exposure: 1.15,
      environmentIntensity: 0.45,
      pixelRatioScale: 0.65,
      pathTrace: {
        enabled: false,
        renderScale: 0.7,
        environmentIntensity: 0.45,
        exposure: 1.15,
        bounces: 8,
        samplesPerFrame: 1,
        filterGlossyFactor: 0.5,
        frameCount: 120,
      },
    }),
  );

export const CONFIGURATOR_VIEWER_DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> =
  deepFreeze(
    createRenderSettings({
      pixelRatioScale: 0.75,
      environmentIntensity: 1.2,
      gtao: { enabled: true },
    }),
  );

/**
 * Cabin configurator + Koshoy storefront: sparse GI v2 on, full resolution up
 * to 2 buffer pixels per CSS pixel (a DPR 3 phone stops at 2). A softer
 * environment map; the key light from the right gives the light and a soft
 * shadow (512² PCF map; lights and softness: `products/cabin-configurator/cabinLook.ts`).
 */
export const CABIN_DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> =
  deepFreeze(
    createRenderSettings({
      pixelRatioScale: Math.min(1, 2 / (globalThis.devicePixelRatio || 1)),
      environmentIntensity: 0.7,
      ssgiSparse: { enabled: true, version: "v2", giStride: 16 },
      shadows: { enabled: true, quality: "low" },
    }),
  );

export const DEFAULT_RENDER_SETTINGS_BY_SCOPE: Record<
  RenderSettingsProductScope,
  Readonly<RenderSettings>
> = {
  shared: DEFAULT_RENDER_SETTINGS,
  roomDesigner: ROOM_DESIGNER_DEFAULT_RENDER_SETTINGS,
  configurator: CONFIGURATOR_DEFAULT_RENDER_SETTINGS,
  configuratorViewer: CONFIGURATOR_VIEWER_DEFAULT_RENDER_SETTINGS,
  modelViewer: MODEL_VIEWER_DEFAULT_RENDER_SETTINGS,
  interiorStudy: INTERIOR_STUDY_DEFAULT_RENDER_SETTINGS,
  cabin: CABIN_DEFAULT_RENDER_SETTINGS,
};

const TONE_MAPPINGS: Record<ToneMappingName, ToneMapping> = {
  none: NoToneMapping,
  linear: LinearToneMapping,
  reinhard: ReinhardToneMapping,
  cineon: CineonToneMapping,
  aces: ACESFilmicToneMapping,
};

const SHADOW_MAP_SIZES: Record<ShadowQuality, number> = {
  low: 512,
  medium: 1024,
  high: 2048,
};

/**
 * Raster AO/GI (SSGI / sparse GI / GTAO) and path tracing are mutually exclusive.
 */
function enforcePipelineExclusivity(settings: RenderSettings): RenderSettings {
  let next = settings;
  if (next.pathTrace.enabled) {
    next = {
      ...next,
      ssgi: { ...next.ssgi, enabled: false },
      ssgiSparse: { ...next.ssgiSparse, enabled: false },
      gtao: { ...next.gtao, enabled: false },
      taa: { ...next.taa, enabled: false },
    };
  } else if (next.ssgi.enabled) {
    next = {
      ...next,
      ssgiSparse: { ...next.ssgiSparse, enabled: false },
      gtao: { ...next.gtao, enabled: false },
    };
  } else if (next.ssgiSparse.enabled) {
    next = {
      ...next,
      ssgi: { ...next.ssgi, enabled: false },
      gtao: { ...next.gtao, enabled: false },
    };
  } else if (next.gtao.enabled) {
    next = {
      ...next,
      ssgi: { ...next.ssgi, enabled: false },
      ssgiSparse: { ...next.ssgiSparse, enabled: false },
    };
  }
  return next;
}

function diffSettings(
  settings: RenderSettings,
  base: Readonly<RenderSettings>,
): SettingsOverrides {
  const result: SettingsOverrides = {};
  const scalarKeys = [
    "toneMapping",
    "exposure",
    "environmentIntensity",
    "environmentUrl",
    "aoMapIntensity",
    "pixelRatioScale",
  ] as const;
  for (const key of scalarKeys) {
    if (settings[key] !== base[key]) {
      (result as Record<string, unknown>)[key] = settings[key];
    }
  }
  for (const section of [
    "ssgi",
    "ssgiSparse",
    "gtao",
    "pathTrace",
    "taa",
    "aa",
    "lights",
    "shadows",
  ] as const) {
    const sectionDiff: Record<string, unknown> = {};
    for (const key of Object.keys(settings[section])) {
      const typedKey = key as keyof typeof settings[typeof section];
      if (settings[section][typedKey] !== base[section][typedKey]) {
        sectionDiff[key] = settings[section][typedKey];
      }
    }
    if (Object.keys(sectionDiff).length > 0) {
      (result as Record<string, unknown>)[section] = sectionDiff;
    }
  }
  return result;
}

export class RenderSettingsStore extends EventTarget {
  private static _instance?: RenderSettingsStore;
  /** Efektif ayar: kullanıcı ayarı + geçici (persist edilmeyen) katman. */
  private _settings: RenderSettings;
  /** Kullanıcının kalıcı ayarı (localStorage'a yazılan). */
  private _user: RenderSettings;
  /** Geçici katman (örn. kumaş bazlı ışık preseti); localStorage'a yazılmaz. */
  private _transient: TransientSettings = {};
  private _productScope: RenderSettingsProductScope = "shared";

  static get instance(): RenderSettingsStore {
    if (!this._instance) this._instance = new RenderSettingsStore();
    return this._instance;
  }

  private constructor() {
    super();
    this._user = mergeSettings(this._readOverrides(), this._baseDefaults());
    this._settings = this._withTransient();
  }

  get productScope(): RenderSettingsProductScope {
    return this._productScope;
  }

  private _baseDefaults(): Readonly<RenderSettings> {
    return DEFAULT_RENDER_SETTINGS_BY_SCOPE[this._productScope];
  }

  /**
   * Switch this page's settings bag + defaults. Hosts call this on connect.
   */
  setProductScope(scope: RenderSettingsProductScope): void {
    if (this._productScope === scope) return;
    this._productScope = scope;
    this._user = mergeSettings(this._readOverrides(), this._baseDefaults());
    this._settings = this._withTransient();
    this.dispatchEvent(
      new CustomEvent<Readonly<RenderSettings>>(
        RENDER_SETTINGS_CHANGED_EVENT,
        { detail: this._settings },
      ),
    );
  }

  get value(): Readonly<RenderSettings> {
    return this._settings;
  }

  update(patch: SettingsOverrides): void {
    let next = mergeSettings(
      {
        ...this._user,
        ...patch,
        ssgi: { ...this._user.ssgi, ...patch.ssgi },
        ssgiSparse: { ...this._user.ssgiSparse, ...patch.ssgiSparse },
        gtao: { ...this._user.gtao, ...patch.gtao },
        pathTrace: { ...this._user.pathTrace, ...patch.pathTrace },
        taa: { ...this._user.taa, ...patch.taa },
        aa: { ...this._user.aa, ...patch.aa },
        lights: { ...this._user.lights, ...patch.lights },
        shadows: { ...this._user.shadows, ...patch.shadows },
      },
      this._baseDefaults(),
    );

    // Explicit enable wins among pipeline modes.
    if (patch.pathTrace?.enabled === true) {
      next = {
        ...next,
        pathTrace: { ...next.pathTrace, enabled: true },
        ssgi: { ...next.ssgi, enabled: false },
        ssgiSparse: { ...next.ssgiSparse, enabled: false },
        gtao: { ...next.gtao, enabled: false },
        taa: { ...next.taa, enabled: false },
      };
    } else if (patch.ssgi?.enabled === true) {
      next = {
        ...next,
        ssgi: { ...next.ssgi, enabled: true },
        ssgiSparse: { ...next.ssgiSparse, enabled: false },
        gtao: { ...next.gtao, enabled: false },
        pathTrace: { ...next.pathTrace, enabled: false },
      };
    } else if (patch.ssgiSparse?.enabled === true) {
      next = {
        ...next,
        ssgiSparse: { ...next.ssgiSparse, enabled: true },
        ssgi: { ...next.ssgi, enabled: false },
        gtao: { ...next.gtao, enabled: false },
        pathTrace: { ...next.pathTrace, enabled: false },
      };
    } else if (patch.gtao?.enabled === true) {
      next = {
        ...next,
        gtao: { ...next.gtao, enabled: true },
        ssgi: { ...next.ssgi, enabled: false },
        ssgiSparse: { ...next.ssgiSparse, enabled: false },
        pathTrace: { ...next.pathTrace, enabled: false },
      };
    }

    this._user = next;
    this._settings = this._withTransient();
    this._persistOverrides();
    this.dispatchEvent(
      new CustomEvent<Readonly<RenderSettings>>(
        RENDER_SETTINGS_CHANGED_EVENT,
        { detail: this._settings },
      ),
    );
  }

  /**
   * Geçici katmanı değiştirir (null = temizle). Kullanıcı ayarının üstüne biner,
   * localStorage'a yazılmaz.
   */
  setTransient(patch: TransientSettings | null): void {
    this._transient = { ...(patch ?? {}) };
    this._settings = this._withTransient();
    this.dispatchEvent(
      new CustomEvent<Readonly<RenderSettings>>(
        RENDER_SETTINGS_CHANGED_EVENT,
        { detail: this._settings },
      ),
    );
  }

  private _withTransient(): RenderSettings {
    const entries = Object.entries(this._transient).filter(
      ([, value]) => value != null,
    );
    return entries.length
      ? { ...this._user, ...Object.fromEntries(entries) }
      : this._user;
  }

  reset(): void {
    this._user = mergeSettings({}, this._baseDefaults());
    this._settings = this._withTransient();
    try {
      localStorage.removeItem(storageKeyFor(this._productScope));
      localStorage.removeItem("sugar-room-designer.render-settings.v1");
      localStorage.removeItem("sugar-room-designer.render-settings.v2");
      localStorage.removeItem("sugar-room-designer.render-settings.v3");
    } catch {}
    this.dispatchEvent(
      new CustomEvent<Readonly<RenderSettings>>(
        RENDER_SETTINGS_CHANGED_EVENT,
        { detail: this._settings },
      ),
    );
  }

  resolvePixelRatio(dpr = window.devicePixelRatio || 1): number {
    const scale = Math.min(1, Math.max(0.25, this._settings.pixelRatioScale));
    return Math.min(4, Math.max(0.25, dpr * scale));
  }

  applyRenderer(renderer: ConfigurableRenderer): void {
    renderer.toneMapping = TONE_MAPPINGS[this._settings.toneMapping];
    renderer.toneMappingExposure = this._settings.exposure;
    renderer.setPixelRatio?.(this.resolvePixelRatio());
    const msaaSamples = this._settings.aa.msaa
      ? Math.min(8, Math.max(1, Math.round(this._settings.aa.samples || 1)))
      : 0;
    Reflect.set(renderer, "_samples", msaaSamples);
    if (renderer.shadowMap) {
      renderer.shadowMap.enabled = this._settings.shadows.enabled;
      renderer.shadowMap.type =
        this._settings.shadows.quality === "low"
          ? PCFShadowMap
          : PCFSoftShadowMap;
    }
  }

  applyScene(scene: Scene): void {
    scene.environmentIntensity = this._settings.environmentIntensity;
    scene.traverse((object) => {
      if (object instanceof Mesh) {
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach((material) => this.applyMaterial(material));
      }
      if ((object as Light).isLight) this.applyLight(object as Light);
    });
  }

  applyMaterial(material: Material): void {
    if (
      material instanceof MeshStandardMaterial ||
      material instanceof MeshPhysicalMaterial ||
      (material as { isNodeMaterial?: boolean }).isNodeMaterial
    ) {
      const pbr = material as MeshStandardMaterial;
      const env = this._settings.environmentIntensity;
      const ao = this._settings.aoMapIntensity;
      let changed = false;
      if (pbr.envMapIntensity !== env) {
        pbr.envMapIntensity = env;
        changed = true;
      }
      if (pbr.aoMap && pbr.aoMapIntensity !== ao) {
        pbr.aoMapIntensity = ao;
        changed = true;
      }
      if (changed) pbr.needsUpdate = true;
    }
  }

  applyLight(light: Light): void {
    if (!("castShadow" in light)) return;
    const shadow = (
      light as Light & {
        shadow?: {
          mapSize: { x: number; y: number; setScalar: (size: number) => void };
          needsUpdate?: boolean;
        };
      }
    ).shadow;
    if (!shadow) return;
    const size = SHADOW_MAP_SIZES[this._settings.shadows.quality];
    // Do not dispose shadow.map here. On WebGPU, PointShadowNode still holds the
    // same CubeRenderTarget (PointShadowDepthTexture); disposing mid-frame causes
    // "Destroyed texture … used in a submit". Size changes are applied via
    // mapSize + needsUpdate; the node resizes the RT on the next shadow pass.
    if (shadow.mapSize.x === size && shadow.mapSize.y === size) return;
    shadow.mapSize.setScalar(size);
    shadow.needsUpdate = true;
  }

  private _readOverrides(): SettingsOverrides {
    try {
      const key = storageKeyFor(this._productScope);
      let raw = localStorage.getItem(key);
      // Migrate legacy shared key once into the new shared scope.
      if (!raw && this._productScope === "shared") {
        raw =
          localStorage.getItem("sugar-room-designer.render-settings.v3") ??
          localStorage.getItem("sugar-room-designer.render-settings.v2");
      }
      return raw ? (JSON.parse(raw) as SettingsOverrides) : {};
    } catch {
      return {};
    }
  }

  private _persistOverrides(): void {
    try {
      const key = storageKeyFor(this._productScope);
      const overrides = diffSettings(this._user, this._baseDefaults());
      if (Object.keys(overrides).length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(overrides));
      }
    } catch {}
  }
}

export const renderSettingsStore = RenderSettingsStore.instance;
